import React, { useEffect, useRef, useState } from 'react';
import './GlobalTitleTooltip.css';

const GAP = 10;
const EDGE_PADDING = 12;

const readTooltipText = (element) => {
  if (!element) return '';

  const title = element.getAttribute('title');
  if (title) {
    element.dataset.arrowTooltip = title;
    element.removeAttribute('title');
    return title;
  }

  return element.dataset.arrowTooltip || '';
};

const isTooltipSuppressed = (element) =>
  Boolean(
    element?.closest(
      '[aria-expanded="true"], [data-arrow-menu-open="true"], [data-arrow-tooltip-disabled="true"]'
    )
  );

const getTooltipPosition = (target, tooltipWidth = 0) => {
  const rect = target.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const centerY = rect.top + rect.height / 2;
  const rightSpace = viewportWidth - rect.right;
  const leftSpace = rect.left;

  let placement = 'top';
  let top = rect.top - GAP;
  let left = rect.left + rect.width / 2;

  if (leftSpace < 100) {
    placement = 'right';
    top = centerY;
    left = rect.right + GAP;
  } else if (rightSpace < 100) {
    placement = 'left';
    top = centerY;
    left = rect.left - GAP;
  } else if (rect.top < 64) {
    placement = 'bottom';
    top = rect.bottom + GAP;
    left = rect.left + rect.width / 2;
  }

  if ((placement === 'top' || placement === 'bottom') && tooltipWidth) {
    const halfWidth = tooltipWidth / 2;
    left = Math.max(EDGE_PADDING + halfWidth, Math.min(viewportWidth - EDGE_PADDING - halfWidth, left));
  }

  top = Math.max(EDGE_PADDING, Math.min(viewportHeight - EDGE_PADDING, top));

  return { top, left, placement };
};

const GlobalTitleTooltip = () => {
  const activeTargetRef = useRef(null);
  const [tooltip, setTooltip] = useState({
    visible: false,
    text: '',
    top: 0,
    left: 0,
    placement: 'top',
  });

  useEffect(() => {
    let showTimer = null;

    const clearShowTimer = () => {
      if (showTimer) {
        window.clearTimeout(showTimer);
        showTimer = null;
      }
    };

    const hideTooltip = () => {
      clearShowTimer();
      activeTargetRef.current = null;
      setTooltip((current) => ({ ...current, visible: false }));
    };

    const showTooltip = (target) => {
      if (isTooltipSuppressed(target)) {
        hideTooltip();
        return;
      }

      const text = readTooltipText(target);
      if (!text) return;

      activeTargetRef.current = target;
      const nextPosition = getTooltipPosition(target);
      clearShowTimer();
      showTimer = window.setTimeout(() => {
        if (!target.isConnected || isTooltipSuppressed(target)) {
          hideTooltip();
          return;
        }

        setTooltip({
          visible: true,
          text,
          ...nextPosition,
        });
      }, 180);
    };

    const handlePointerOver = (event) => {
      const target = event.target.closest('[title], [data-arrow-tooltip]');
      if (!target || target === activeTargetRef.current) return;
      showTooltip(target);
    };

    const handlePointerOut = (event) => {
      if (!activeTargetRef.current) return;
      if (event.relatedTarget && activeTargetRef.current.contains(event.relatedTarget)) return;
      hideTooltip();
    };

    const handleFocusIn = (event) => {
      const target = event.target.closest('[title], [data-arrow-tooltip]');
      if (target) showTooltip(target);
    };

    const handleFocusOut = () => hideTooltip();
    const handleEscape = (event) => {
      if (event.key === 'Escape') hideTooltip();
    };

    document.addEventListener('pointerdown', hideTooltip, true);
    document.addEventListener('arrow-hide-tooltip', hideTooltip);
    document.addEventListener('mouseover', handlePointerOver, true);
    document.addEventListener('mouseout', handlePointerOut, true);
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('focusout', handleFocusOut, true);
    document.addEventListener('keydown', handleEscape, true);
    window.addEventListener('scroll', hideTooltip, true);
    window.addEventListener('resize', hideTooltip);

    return () => {
      clearShowTimer();
      document.removeEventListener('pointerdown', hideTooltip, true);
      document.removeEventListener('arrow-hide-tooltip', hideTooltip);
      document.removeEventListener('mouseover', handlePointerOver, true);
      document.removeEventListener('mouseout', handlePointerOut, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('focusout', handleFocusOut, true);
      document.removeEventListener('keydown', handleEscape, true);
      window.removeEventListener('scroll', hideTooltip, true);
      window.removeEventListener('resize', hideTooltip);
    };
  }, []);

  useEffect(() => {
    if (!tooltip.visible) return;
    const tooltipElement = document.querySelector('.arrow-global-tooltip');
    const target = activeTargetRef.current;

    if (!tooltipElement || !target) return;

    const nextPosition = getTooltipPosition(target, tooltipElement.offsetWidth);
    setTooltip((current) => ({
      ...current,
      ...nextPosition,
    }));
  }, [tooltip.visible, tooltip.text]);

  if (!tooltip.text) return null;

  return (
    <div
      className={`arrow-global-tooltip ${tooltip.visible ? 'visible' : ''} placement-${tooltip.placement}`}
      style={{ top: tooltip.top, left: tooltip.left }}
      role="tooltip"
    >
      {tooltip.text}
    </div>
  );
};

export default GlobalTitleTooltip;
